import React, { useEffect, useState } from "react";
import {
    getAuth,
    onAuthStateChanged,
    updatePassword,
    signOut,
    EmailAuthProvider,
    reauthenticateWithCredential,
} from "firebase/auth";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

/* ─── THEME TOKENS ─── */
const THEMES = {
    light: {
        bg: "#ffffff",
        bgSub: "#f7f7f8",
        surface: "#f0f0f2",
        surfaceHover: "#e8e8eb",
        border: "#e3e3e6",
        borderStrong: "#c5c5ca",
        text: "#111111",
        textSub: "#5f5f6b",
        textMuted: "#a3a3b0",
        userBubble: "#1a1a1a",
        userText: "#ffffff",
        aiBubble: "#f7f7f8",
        aiBorder: "#e8e8eb",
        aiText: "#111111",
        accent: "#1a1a1a",
        accentText: "#ffffff",
        danger: "#d93025",
        success: "#188038",
        inputBg: "#f7f7f8",
        inputBorder: "#e3e3e6",
        sidebarBg: "#f7f7f8",
        sidebarBorder: "#e3e3e6",
        pillActive: "#1a1a1a",
        pillActiveText: "#ffffff",
        pillBg: "#ebebed",
        pillText: "#5f5f6b",
        shadow: "0 1px 3px rgba(0,0,0,0.07), 0 1px 2px rgba(0,0,0,0.05)",
        shadowMd: "0 4px 20px rgba(0,0,0,0.08)",
    },
    dark: {
        bg: "#212121",
        bgSub: "#2a2a2a",
        surface: "#303030",
        surfaceHover: "#3a3a3a",
        border: "#3a3a3a",
        borderStrong: "#4e4e4e",
        text: "#ececec",
        textSub: "#9e9e9e",
        textMuted: "#616161",
        userBubble: "#f0f0f0",
        userText: "#111111",
        aiBubble: "#2a2a2a",
        aiBorder: "#3a3a3a",
        aiText: "#ececec",
        accent: "#ececec",
        accentText: "#111111",
        danger: "#f28b82",
        success: "#81c995",
        inputBg: "#2a2a2a",
        inputBorder: "#3a3a3a",
        sidebarBg: "#1a1a1a",
        sidebarBorder: "#2e2e2e",
        pillActive: "#ececec",
        pillActiveText: "#111111",
        pillBg: "#303030",
        pillText: "#9e9e9e",
        shadow: "0 1px 3px rgba(0,0,0,0.3)",
        shadowMd: "0 4px 20px rgba(0,0,0,0.35)",
    },
};

/* ─── DYNAMIC GLOBAL STYLES ─── */
const buildCSS = (t) => `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; font-family: 'Google Sans Text', 'Google Sans', sans-serif; background: ${t.bg}; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: ${t.border}; border-radius: 8px; }

  @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  @keyframes spin   { to{transform:rotate(360deg)} }

  .fade-up { animation: fadeUp 0.25s ease forwards; }
  .fade-in { animation: fadeIn 0.2s ease; }

  .sb-btn {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 12px; border-radius: 10px; border: none;
    background: transparent; color: ${t.textSub};
    font-family: 'Google Sans', sans-serif; font-size: 13.5px; font-weight: 500;
    cursor: pointer; width: 100%; text-align: left;
    transition: background 0.12s, color 0.12s, padding-left 0.12s;
  }
  .sb-btn:hover  { background: ${t.surface}; color: ${t.text}; padding-left: 16px; }
  .sb-btn.danger { color: ${t.danger}; }
  .sb-btn.danger:hover { background: ${t.danger}18; }

  .icon-btn {
    display: flex; align-items: center; justify-content: center;
    width: 36px; height: 36px; border-radius: 8px; border: none;
    background: transparent; color: ${t.textSub};
    cursor: pointer; transition: background 0.12s, color 0.12s; flex-shrink: 0;
  }
  .icon-btn:hover { background: ${t.surface}; color: ${t.text}; }

  .field {
    width: 100%;
    background: ${t.inputBg};
    border: 1px solid ${t.inputBorder};
    color: ${t.text};
    border-radius: 14px;
    padding: 15px 16px;
    font-size: 14.5px;
    font-family: 'Google Sans Text', 'Google Sans', sans-serif;
    outline: none;
    transition: border-color 0.12s, background 0.12s;
    min-height: 52px;
  }
  .field:focus {
    border-color: ${t.borderStrong};
  }
  .field::placeholder {
    color: ${t.textMuted};
  }
  .field[readonly] {
    opacity: 0.8;
    cursor: not-allowed;
  }

  .save-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border: none; border-radius: 12px;
    background: ${t.accent}; color: ${t.accentText};
    padding: 13px 18px;
    font-weight: 600; font-size: 14px;
    cursor: pointer;
    transition: opacity 0.12s, transform 0.12s;
    font-family: 'Google Sans', sans-serif;
  }
  .save-btn:hover:not(:disabled) { opacity: .88; transform: translateY(-1px); }
  .save-btn:disabled { opacity: .55; cursor: not-allowed; }

  .ghost-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border: 1px solid ${t.border}; border-radius: 12px;
    background: ${t.bg}; color: ${t.text};
    padding: 13px 18px;
    font-weight: 600; font-size: 14px;
    cursor: pointer;
    font-family: 'Google Sans', sans-serif;
    transition: background 0.12s, border-color 0.12s;
  }
  .ghost-btn:hover { background: ${t.surface}; border-color: ${t.borderStrong}; }

  .material-icons-round { font-size: 20px; user-select: none; line-height: 1; }
`;

export default function Profile() {
    const [dark, setDark] = useState(() => localStorage.getItem("darkMode") === "true");
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [mobile, setMobile] = useState(window.innerWidth <= 768);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [firebaseUser, setFirebaseUser] = useState(null);

    const [form, setForm] = useState({
        fullName: "",
        email: "",
        phone: "",
        dob: "",
        gender: "",
        country: "",
    });

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");

    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const T = dark ? THEMES.dark : THEMES.light;
    const auth = getAuth();
    const db = getFirestore();

    useEffect(() => {
        const fn = () => setMobile(window.innerWidth <= 768);
        window.addEventListener("resize", fn);
        return () => window.removeEventListener("resize", fn);
    }, []);

    useEffect(() => {
        localStorage.setItem("darkMode", dark);
    }, [dark]);

    useEffect(() => {
        const styleId = "omrali-profile-theme";
        let styleTag = document.getElementById(styleId);

        if (!styleTag) {
            styleTag = document.createElement("style");
            styleTag.id = styleId;
            document.head.appendChild(styleTag);
        }

        styleTag.innerHTML = buildCSS(T);
    }, [dark, T]);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) {
                window.location.href = "/login";
                return;
            }

            setFirebaseUser(user);

            try {
                const ref = doc(db, "users", user.uid);
                const snap = await getDoc(ref);

                if (snap.exists()) {
                    const data = snap.data();

                    setForm({
                        fullName: data.fullName || data.name || "",
                        email: user.email || data.email || "",
                        phone: data.phone || data.phoneNumber || "",
                        dob: data.dob || "",
                        gender: data.gender || "",
                        country: data.country || "",
                    });

                    localStorage.setItem("email", user.email || data.email || "");
                    localStorage.setItem("name", data.fullName || data.name || "");
                    localStorage.setItem("userId", user.uid);
                } else {
                    const initial = {
                        fullName: user.displayName || localStorage.getItem("name") || "",
                        email: user.email || localStorage.getItem("email") || "",
                        phone: "",
                        dob: "",
                        gender: "",
                        country: "",
                    };

                    setForm(initial);

                    await setDoc(
                        ref,
                        {
                            fullName: initial.fullName,
                            email: initial.email,
                            phone: "",
                            dob: "",
                            gender: "",
                            country: "",
                            createdAt: serverTimestamp(),
                            updatedAt: serverTimestamp(),
                        },
                        { merge: true }
                    );

                    localStorage.setItem("email", initial.email);
                    localStorage.setItem("name", initial.fullName);
                    localStorage.setItem("userId", user.uid);
                }
            } catch {
                setErr("Failed to load profile.");
            } finally {
                setLoading(false);
            }
        });

        return () => unsub();
    }, [auth, db]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((p) => ({ ...p, [name]: value }));
    };

    const [currentPassword, setCurrentPassword] = useState("");
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

    const handleSave = async (e) => {
        e.preventDefault();
        setErr("");
        setMsg("");

        if (!firebaseUser) return;

        const storedEmail = localStorage.getItem("email");
        const storedUid = localStorage.getItem("userId");

        if (!storedEmail || !storedUid) {
            setErr("Session data missing. Please log in again.");
            return;
        }

        if (storedUid !== firebaseUser.uid) {
            setErr("User session mismatch. Please log in again.");
            return;
        }

        if (!form.fullName.trim()) {
            setErr("Full name is required.");
            return;
        }

        if (newPassword || confirmPassword) {
            if (newPassword.length < 6) {
                setErr("New password must be at least 6 characters.");
                return;
            }

            if (newPassword !== confirmPassword) {
                setErr("Passwords do not match.");
                return;
            }
        }

        // ✅ First click = reveal password box only
        if (!showPasswordConfirm) {
            setShowPasswordConfirm(true);
            setErr("Please enter your current password to confirm changes.");
            return;
        }

        // ✅ Second click = require password
        if (!currentPassword.trim()) {
            setErr("Please enter your current password to save changes.");
            return;
        }

        setSaving(true);

        try {
            const credential = EmailAuthProvider.credential(storedEmail, currentPassword);
            await reauthenticateWithCredential(firebaseUser, credential);

            const ref = doc(db, "users", firebaseUser.uid);

            await setDoc(
                ref,
                {
                    fullName: form.fullName.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    dob: form.dob,
                    gender: form.gender,
                    country: form.country.trim(),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            if (newPassword) {
                await updatePassword(firebaseUser, newPassword);
            }

            localStorage.setItem("name", form.fullName.trim());
            localStorage.setItem("email", form.email.trim());
            localStorage.setItem("userId", firebaseUser.uid);

            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setShowPasswordConfirm(false);

            setErr("");
            setMsg(newPassword ? "Profile and password updated successfully." : "Profile updated successfully.");
        } catch (e) {
            if (
                e.code === "auth/wrong-password" ||
                e.code === "auth/invalid-credential" ||
                e.code === "auth/invalid-login-credentials"
            ) {
                setErr("Current password is incorrect.");
            } else if (e.code === "auth/too-many-requests") {
                setErr("Too many attempts. Please try again later.");
            } else if (e.code === "auth/requires-recent-login") {
                setErr("Please log in again before making sensitive changes.");
            } else {
                setErr(e.message || "Failed to update profile.");
            }
        } finally {
            setSaving(false);
        }
    };
    const logout = async () => {
        try {
            await signOut(auth);
        } catch {
            // ignore
        }
        localStorage.removeItem("email");
        localStorage.removeItem("name");
        localStorage.removeItem("userId");
        window.location.href = "/";
    };

    const Icon = ({ n, style }) => (
        <span className="material-icons-round" style={style}>{n}</span>
    );

    const Logo = () => (
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: 7,
                    background: T.accent,
                    color: T.accentText,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 14,
                    fontFamily: "'Google Sans', sans-serif",
                    flexShrink: 0,
                }}
            >
                O
            </div>
            <span
                style={{
                    fontWeight: 700,
                    fontSize: 15.5,
                    color: T.text,
                    fontFamily: "'Google Sans', sans-serif",
                    letterSpacing: "-0.3px",
                }}
            >
                Omrali <span style={{ fontWeight: 400, color: T.textSub }}>AI</span>
            </span>
        </div>
    );

    const Sidebar = () => (
        <>
            {mobile && sidebarOpen && (
                <div
                    onClick={() => setSidebarOpen(false)}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,.4)",
                        zIndex: 98,
                    }}
                    className="fade-in"
                />
            )}

            <aside
                style={{
                    width: mobile ? 256 : 224,
                    background: T.sidebarBg,
                    borderRight: `1px solid ${T.sidebarBorder}`,
                    display: "flex",
                    flexDirection: "column",
                    padding: "14px 8px",
                    position: mobile ? "fixed" : "sticky",
                    top: 0,
                    left: 0,
                    height: "100vh",
                    zIndex: 99,
                    overflowY: "auto",
                    flexShrink: 0,
                    transform: mobile ? (sidebarOpen ? "translateX(0)" : "translateX(-100%)") : "none",
                    transition: "transform 0.22s cubic-bezier(.4,0,.2,1)",
                }}
            >
                <div style={{ padding: "2px 8px 14px", borderBottom: `1px solid ${T.border}`, marginBottom: 6 }}>
                    <Logo />
                </div>

                <div
                    style={{
                        margin: "0 4px 8px",
                        padding: "8px 11px",
                        borderRadius: 10,
                        background: T.surface,
                        border: `1px solid ${T.border}`,
                    }}
                >
                    <div
                        style={{
                            fontSize: 11,
                            color: T.textMuted,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            letterSpacing: ".05em",
                        }}
                    >
                        Signed in
                    </div>
                    <div
                        style={{
                            fontSize: 13.5,
                            color: T.text,
                            marginTop: 1,
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {form.fullName || "User"}
                    </div>
                </div>

                {[
                    { icon: "chat", label: "Back to Chat", fn: () => { window.location.href = "/"; } },
                    { icon: "history", label: "History", fn: () => { window.location.href = "/history"; } },
                    { icon: "download", label: "Download Chats", fn: () => { window.location.href = "/download"; } },
                    { icon: "info", label: "About", fn: () => { window.location.href = "/about"; } },
                    { icon: "mail", label: "Contact", fn: () => { window.location.href = "/contact"; } },
                ].map(({ icon, label, fn }) => (
                    <button key={label} className="sb-btn" onClick={() => { fn(); setSidebarOpen(false); }}>
                        <Icon n={icon} style={{ fontSize: 18, opacity: 0.75 }} />
                        {label}
                    </button>
                ))}

                <hr style={{ border: "none", borderTop: `1px solid ${T.border}`, margin: "6px 4px" }} />

                <button className="sb-btn" onClick={() => setDark((d) => !d)}>
                    <Icon n={dark ? "light_mode" : "dark_mode"} style={{ fontSize: 18, opacity: 0.75 }} />
                    {dark ? "Light mode" : "Dark mode"}
                </button>

                <div style={{ flex: 1 }} />

                <button className="sb-btn danger" onClick={logout}>
                    <Icon n="logout" style={{ fontSize: 18 }} />
                    Logout
                </button>

                <div style={{ fontSize: 11, color: T.textMuted, textAlign: "center", padding: "10px 4px 2px" }}>
                    © 2025 Omrali AI
                </div>
            </aside>
        </>
    );

    return (
        <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: T.bg, color: T.text }}>
            <Sidebar />

            {mobile && (
                <button
                    className="icon-btn"
                    onClick={() => setSidebarOpen((p) => !p)}
                    style={{
                        position: "fixed",
                        top: 11,
                        left: 11,
                        zIndex: 100,
                        background: T.bg,
                        border: `1px solid ${T.border}`,
                        borderRadius: 8,
                        width: 38,
                        height: 38,
                    }}
                >
                    <Icon n={sidebarOpen ? "close" : "menu"} />
                </button>
            )}

            <main
                style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100vh",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                {/* Header */}
                <header
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: mobile ? "9px 14px 9px 56px" : "9px 20px",
                        borderBottom: `1px solid ${T.border}`,
                        background: T.bg,
                        flexShrink: 0,
                        minHeight: 50,
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {!mobile && <Logo />}
                        <span
                            style={{
                                fontSize: 12,
                                color: T.textMuted,
                                fontWeight: 500,
                                marginLeft: mobile ? 0 : 6,
                            }}
                        >
                            Manage your account details
                        </span>
                    </div>

                    <button className="icon-btn" onClick={() => setDark((d) => !d)} title="Toggle theme">
                        <Icon n={dark ? "light_mode" : "dark_mode"} />
                    </button>
                </header>

                {/* Content */}
                <div
                    style={{
                        flex: 1,
                        overflowY: "auto",
                        padding: mobile ? "18px 14px 24px" : "24px 28px 28px",
                    }}
                >
                    <div
                        className="fade-up"
                        style={{
                            maxWidth: 860,
                            margin: "0 auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                        }}
                    >
                        {/* Hero card */}
                        <div
                            style={{
                                background: T.aiBubble,
                                border: `1px solid ${T.aiBorder}`,
                                borderRadius: 18,
                                padding: mobile ? "18px" : "22px",
                                boxShadow: T.shadow,
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                                <div
                                    style={{
                                        width: 54,
                                        height: 54,
                                        borderRadius: 14,
                                        background: T.accent,
                                        color: T.accentText,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontWeight: 700,
                                        fontSize: 20,
                                        fontFamily: "'Google Sans', sans-serif",
                                        flexShrink: 0,
                                    }}
                                >
                                    {(form.fullName?.[0] || "U").toUpperCase()}
                                </div>

                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            fontSize: mobile ? 22 : 26,
                                            fontWeight: 700,
                                            color: T.text,
                                            letterSpacing: "-0.4px",
                                        }}
                                    >
                                        {loading ? "Loading profile..." : (form.fullName || "Your Profile")}
                                    </div>
                                    <div
                                        style={{
                                            color: T.textSub,
                                            marginTop: 4,
                                            fontSize: 14,
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {form.email || "No email available"}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form card */}
                        <form
                            onSubmit={handleSave}
                            style={{
                                background: T.bgSub,
                                border: `1px solid ${T.border}`,
                                borderRadius: 18,
                                padding: mobile ? "16px" : "22px",
                                boxShadow: T.shadow,
                                display: "grid",
                                gap: 16,
                            }}
                        >
                            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                                Profile Details
                            </div>

                            {loading ? (
                                <div
                                    style={{
                                        color: T.textSub,
                                        fontSize: 14,
                                        padding: "12px 0",
                                    }}
                                >
                                    Loading your profile...
                                </div>
                            ) : (
                                <>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
                                            gap: 14,
                                        }}
                                    >
                                        <div>
                                            <div style={labelStyle(T)}>Full Name</div>
                                            <input
                                                className="field"
                                                type="text"
                                                name="fullName"
                                                value={form.fullName}
                                                onChange={handleChange}
                                                placeholder="Enter your full name"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <div style={labelStyle(T)}>Email</div>
                                            <input
                                                className="field"
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                readOnly
                                                placeholder="Email"
                                            />
                                            <div
                                                style={{
                                                    marginTop: 8,
                                                    fontSize: 12.5,
                                                    color: T.textMuted,
                                                    lineHeight: 1.5,
                                                    paddingLeft: 2,
                                                }}
                                            >
                                                To change email, contact us.
                                            </div>
                                        </div>

                                        <div>
                                            <div style={labelStyle(T)}>Phone Number</div>
                                            <input
                                                className="field"
                                                type="tel"
                                                name="phone"
                                                value={form.phone}
                                                onChange={handleChange}
                                                placeholder="Enter your phone number"
                                            />
                                        </div>

                                        <div>
                                            <div style={labelStyle(T)}>Date of Birth</div>
                                            <input
                                                className="field"
                                                type="date"
                                                name="dob"
                                                value={form.dob}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div>
                                            <div style={labelStyle(T)}>Gender</div>
                                            <select
                                                className="field"
                                                name="gender"
                                                value={form.gender}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select gender</option>
                                                <option value="Male">Male</option>
                                                <option value="Female">Female</option>
                                                <option value="Other">Other</option>
                                                <option value="Prefer not to say">Prefer not to say</option>
                                            </select>
                                        </div>

                                        <div>
                                            <div style={labelStyle(T)}>Country</div>
                                            <input
                                                className="field"
                                                type="text"
                                                name="country"
                                                value={form.country}
                                                onChange={handleChange}
                                                placeholder="Enter your country"
                                            />
                                        </div>
                                    </div>

                                    <div
                                        style={{
                                            marginTop: 4,
                                            paddingTop: 14,
                                            borderTop: `1px solid ${T.border}`,
                                            display: "grid",
                                            gap: 14,
                                        }}
                                    >
                                        <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                                            Change Password <span style={{ color: T.textSub, fontWeight: 500 }}>(optional)</span>
                                        </div>

                                        <div
                                            style={{
                                                display: "grid",
                                                gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
                                                gap: 14,
                                            }}
                                        >
                                            <div>
                                                <div style={labelStyle(T)}>New Password</div>
                                                <input
                                                    className="field"
                                                    type="password"
                                                    value={newPassword}
                                                    onChange={(e) => setNewPassword(e.target.value)}
                                                    placeholder="Enter new password"
                                                />
                                            </div>

                                            <div>
                                                <div style={labelStyle(T)}>Confirm New Password</div>
                                                <input
                                                    className="field"
                                                    type="password"
                                                    value={confirmPassword}
                                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                                    placeholder="Confirm new password"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {showPasswordConfirm && (
                                        <div
                                            className="fade-up"
                                            style={{
                                                marginTop: 4,
                                                paddingTop: 14,
                                                borderTop: `1px solid ${T.border}`,
                                                display: "grid",
                                                gap: 14,
                                            }}
                                        >
                                            <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>
                                                Confirm Changes
                                            </div>

                                            <div>
                                                <div style={labelStyle(T)}>Current Password</div>
                                                <input
                                                    className="field"
                                                    type="password"
                                                    value={currentPassword}
                                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                                    placeholder="Enter your current password"
                                                    autoComplete="current-password"
                                                    autoFocus
                                                />
                                                <div
                                                    style={{
                                                        marginTop: 8,
                                                        fontSize: 12.5,
                                                        color: T.textMuted,
                                                        lineHeight: 1.5,
                                                        paddingLeft: 2,
                                                    }}
                                                >
                                                    Enter your password to confirm profile changes.
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {err && (
                                        <div
                                            style={{
                                                background: `${T.danger}18`,
                                                color: T.danger,
                                                border: `1px solid ${T.danger}33`,
                                                borderRadius: 12,
                                                padding: "12px 14px",
                                                fontSize: 13.5,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {err}
                                        </div>
                                    )}

                                    {msg && (
                                        <div
                                            style={{
                                                background: `${T.success}18`,
                                                color: T.success,
                                                border: `1px solid ${T.success}33`,
                                                borderRadius: 12,
                                                padding: "12px 14px",
                                                fontSize: 13.5,
                                                lineHeight: 1.5,
                                            }}
                                        >
                                            {msg}
                                        </div>
                                    )}

                                    <div
                                        style={{
                                            display: "flex",
                                            gap: 10,
                                            flexWrap: "wrap",
                                            marginTop: 4,
                                        }}
                                    >
                                        <button type="submit" className="save-btn" disabled={saving}>
                                            <Icon
                                                n={saving ? "hourglass_empty" : "save"}
                                                style={saving ? { animation: "spin 1s linear infinite" } : {}}
                                            />
                                            {saving ? "Saving..." : showPasswordConfirm ? "Confirm & Save" : "Save Changes"}
                                        </button>

                                        <button
                                            type="button"
                                            className="ghost-btn"
                                            onClick={() => (window.location.href = "/contact")}
                                        >
                                            <Icon n="mail" />
                                            Contact Us
                                        </button>
                                    </div>
                                </>
                            )}
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
}

const labelStyle = (T) => ({
    fontSize: 12,
    fontWeight: 600,
    color: T.textSub,
    marginBottom: 7,
    paddingLeft: 2,
    textTransform: "uppercase",
    letterSpacing: ".04em",
});